import { useState, useEffect, useCallback } from 'react';
import { ContextQuestion } from '../../../types';
import { evaluateContextAnswer } from '../../../../../redux/api/questionsApi';
import { ContextQuestionState } from '../utils/types';
import { MAX_CHARACTERS, MIN_CHARACTERS, MESSAGES } from '../utils/constants';
import { isAnswerValid } from '../utils/utils';

interface UseContextQuestionStateProps {
  question: ContextQuestion;
  answeredAnswer?: string;
  resolved?: boolean;
  setText?: (text: string) => void;
  handleSubmit?: () => void;
}

export const useContextQuestionState = ({
  question,
  answeredAnswer,
  resolved,
  setText,
  handleSubmit,
}: UseContextQuestionStateProps) => {
  const [state, setState] = useState<ContextQuestionState>({
    answer: answeredAnswer || '',
    isSubmitting: false,
    isEvaluating: false,
    evaluationResult: question.evaluation || null,
  });

  useEffect(() => {
    setState({
      answer: answeredAnswer || '',
      isSubmitting: false,
      isEvaluating: false,
      evaluationResult: question.evaluation || null,
    });
  }, [question.id_questao, answeredAnswer, question.evaluation]);

  useEffect(() => {
    if (resolved && answeredAnswer) {
      setState((prev) => ({ ...prev, answer: answeredAnswer }));
    }
  }, [resolved, answeredAnswer]);

  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const text = e.target.value;
      if (text.length <= MAX_CHARACTERS) {
        setState((prev) => ({ ...prev, answer: text }));
        setText?.(text);
      }
    },
    [setText]
  );

  const handleSubmitContext = useCallback(async () => {
    if (handleSubmit) {
      handleSubmit();
      return;
    }

    if (!isAnswerValid(state.answer, MIN_CHARACTERS)) {
      alert(MESSAGES.MIN_LENGTH_ERROR);
      return;
    }

    setState((prev) => ({ ...prev, isSubmitting: true, isEvaluating: true }));

    try {
      const result = await evaluateContextAnswer(question.id_questao, state.answer);
      setState((prev) => ({ ...prev, evaluationResult: result }));
    } catch (error) {
      console.error('Erro ao avaliar resposta:', error);
      alert(MESSAGES.EVALUATION_ERROR);
    } finally {
      setState((prev) => ({ ...prev, isSubmitting: false, isEvaluating: false }));
    }
  }, [state.answer, question.id_questao, handleSubmit]);

  return {
    state,
    handleTextChange,
    handleSubmitContext,
  };
};
